import { InputOutputSection } from '@modules/input-output-devices/InputOutputSelector';
import { InputOutputSelector } from '@modules/input-output-devices/InputOutputSelector/InputOutputSelector';
import { VideoCameraInput } from '@modules/input-output-devices/InputOutputSelector/VideoCameraInput/VideoCameraInput';
import { Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/system';
import DetectRTC from 'detectrtc';
import { useEffect } from 'react';
import { useGatheringPermissions } from 'src/hooks/useGatheringPermissions';
import { useLocales } from 'src/locales';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { usePermissionsModal } from './PermissionsModal';
import { SettingsDevicesButton } from './SettingsDevicesButton';
import { VideoActionsButtons } from './VideoActionsButtons';

type settingsButtonType = {
  icon: string;
  title: string;
  onlySection: InputOutputSection;
  status: 'not-present' | 'not-granted' | 'granted';
};

export const ConferenceDevicesVideo = () => {
  const { translate } = useLocales();

  const {
    audioPermission,
    videoPermission,
    hasMicrophone,
    hasCamera,
    isLoading,
  } = useGatheringPermissions();

  const { showPermissionsDialog } = usePermissionsModal();

  const { defaultDevice, changeDevice, preConnectionOptions } =
    useLivekitRTCState();

  const settingsButtons: settingsButtonType[] = [
    {
      onlySection: InputOutputSection.MICROPHONE,
      icon: 'tabler:microphone',
      title: translate('shared.labels.microphone'),
      status: DetectRTC.hasMicrophone
        ? audioPermission === 'granted'
          ? 'granted'
          : 'not-granted'
        : 'not-present',
    },
    {
      onlySection: InputOutputSection.SPEAKER,
      icon: 'tabler:headphones',
      title: translate('shared.labels.speakers'),
      status: DetectRTC.hasSpeakers
        ? audioPermission === 'granted'
          ? 'granted'
          : 'not-granted'
        : 'not-present',
    },
    {
      onlySection: InputOutputSection.CAMERA,
      icon: 'tabler:device-computer-camera',
      title: translate('shared.labels.webcam'),
      status: DetectRTC.hasWebcam
        ? videoPermission === 'granted'
          ? 'granted'
          : 'not-granted'
        : 'not-present',
    },
  ];

  useEffect(() => {
    DetectRTC.load(async () => {
      if (DetectRTC.hasWebcam) {
        if (
          defaultDevice.video === 'default' &&
          DetectRTC.videoInputDevices.length > 0
        ) {
          changeDevice('videoinput', DetectRTC.videoInputDevices[0].id);
        }
      }
    });
  }, [defaultDevice.video, changeDevice]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if ((hasCamera && hasMicrophone) || isLoading) return;
    showPermissionsDialog();
  }, [isLoading]);

  const getTitle = (section: settingsButtonType) => {
    return section.status === 'not-granted'
      ? translate('communicate.rtc_controls.permission_required')
      : section.status === 'not-present'
        ? translate('communicate.rtc_controls.not_present')
        : section.title;
  };

  const getVideoTitle =
    videoPermission === 'granted'
      ? translate('communicate.rtc_controls.camera_off')
      : videoPermission === 'denied'
        ? translate('communicate.rtc_controls.permission_denied_title')
        : translate('communicate.rtc_controls.permission_activate_title');

  const getVidePermissionButtonTitle =
    videoPermission === 'denied'
      ? translate('communicate.rtc_controls.permission_denied_button')
      : translate('communicate.rtc_controls.permission_activate_button');

  return (
    <Container>
      <CameraContainerStyled>
        {DetectRTC.hasWebcam &&
        videoPermission === 'granted' &&
        preConnectionOptions.cameraOn ? (
          <VideoCameraInput deviceId={defaultDevice.video} variant="medium" />
        ) : (
          <PermissionsContainer>
            {DetectRTC.hasWebcam ? (
              <>
                <Typography variant="subtitle1" textAlign="center">
                  {getVideoTitle}
                </Typography>
                {videoPermission !== 'granted' && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={showPermissionsDialog}
                    sx={{ fontSize: '12px' }}
                  >
                    {getVidePermissionButtonTitle}
                  </Button>
                )}
              </>
            ) : (
              <Typography variant="subtitle1">
                {translate('communicate.rtc_controls.no_camera')}
              </Typography>
            )}
          </PermissionsContainer>
        )}
        <VideoActionsButtons />
        <LinearBackgroundStyled />
      </CameraContainerStyled>
      <Stack direction="row" gap={2} px={2} py={3}>
        {settingsButtons.map((section) => (
          <SettingsDevicesButton
            key={section.title}
            popover={InputOutputSelector}
            context={{
              showSections: [section.onlySection],
              isConference: true,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
            }}
            disabled={section.status !== 'granted'}
            icon={section.icon}
            title={getTitle(section)}
          />
        ))}
      </Stack>
    </Container>
  );
};

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: 520,
  maxWidth: 'calc(100vw - 48px)',
}));

const CameraContainerStyled = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 520,
  maxWidth: 'calc(100vw - 48px)',
  aspectRatio: '16 / 9',
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const LinearBackgroundStyled = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  width: '100%',
  height: theme.spacing(10),
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 106.55%)',
  backgroundBlendMode: 'multiply',
  zIndex: 1,
}));

const PermissionsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0, 2),
  marginBottom: theme.spacing(2),
  zIndex: 3,

  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(2),
  },
}));
